import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { ApolloError, useApolloClient, useMutation } from "@apollo/client"

import AbortModalContent from "./AbortModalContent/AbortModalContent"
import { pickClaimingConversionCancelConditionTexts } from "./ClaimingConversionCancelPolicy/ClaimingConversionCancelPolicy.logic"
import LotteryTermsSection from "./LotteryTermsSection/LotteryTermsSection"
import RereservationIncentiveBanner, {
	IncentiveSummary,
	unmaskIncentive,
} from "./RereservationIncentiveBanner/RereservationIncentiveBanner"
import {
	DEFAULT_DURATION_MINUTE,
	getCompletedPath,
	getMenuNameTextsWithPriceAndMinutes,
	useChunks,
} from "./RereservationPage.logic"
import styles from "./RereservationPageV2.module.scss"
import ReservableSlotsV2 from "./ReservableSlots/ReservableSlotsV2"
import { IncentiveProgressInfo } from "./ReservableSlots/types"
import ReservationConfirmModalContent, {
	ReservationDetails,
} from "./ReservationConfirmModalContent/ReservationConfirmModalContent"
import { originalStaffLabel } from "./freeStaff"

import dateImage from "@/lib/images/date.svg"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { ApolloContext } from "@/provider/apollo/context"
import apiService from "@/provider/rest"
import { isUenoClinicDataSource } from "@/public/domain/dataSource"
import {
	getExtensionsCode,
	isSlotUnavailableToCreateReservationErrorCode,
} from "@/public/domain/graphql"
import { useEvent } from "@/public/event"
import { FragmentType, getFragmentData } from "@/public/gql/__generated__"
import { DocumentType, graphql } from "@/public/gql/__generated__/gql"
import {
	formatGraphQLDayToJapaneseMonthDay,
	formatJstDateUntilMinute,
	getJSTISOString,
} from "@/utils/date"
import { toStringWithCommas } from "@/utils/strings/number"

const CanceledReservationV2 = graphql(/* GraphQL */ `
	query GetCanceledReservationV2($param: CanceledReservationV2Param!) {
		canceledReservationV2(param: $param) {
			isRereserved
			salon {
				id
				iconUrl
				headerImageUrl
				name
				cancelConditionTextsForMenu
				cancelConditionTextsForNoMenu
			}
			customer {
				id
				name
				phoneNumber
			}
			canceledReservation {
				cancelId
				reserveId
				visitDate
				price
				durationMinute
				menuOrCouponNames
				equipmentIds
				visitCount
				hidesStaffDueToNotNominated
				dataSource
				useClaimingConversionRecancelPolicy
				incentive {
					...rereservationIncentiveBanner
					...lotteryNoteSection
				}
				enabledFeatures {
					allowsDifferentStaffReservation
					enableAbortEncouragingReservationButton
					enableSlotCaching
					hidesReservationDetails
					showsNextReservationMenuRegularPriceNote
					hidesCancelPolicy
				}
				staff {
					scrapingTargetSiteId
					name
				}
				treatableStaffs {
					...treatableStaff
				}
			}
		}
	}
`)

const CreateRereservationV2 = graphql(/* GraphQL */ `
	mutation CreateRereservationV2($input: CreateRereservationV2Input!) {
		createRereservationV2(input: $input) {
			__typename
		}
	}
`)

// 上野クリニック専用: 予約を確定せず希望枠の「受付」だけを行う
const CreateUenoRereservationRequest = graphql(/* GraphQL */ `
	mutation CreateUenoRereservationRequest(
		$input: CreateUenoRereservationRequestInput!
	) {
		createUenoRereservationRequest(input: $input) {
			__typename
		}
	}
`)

// 抽選 (LOTTERY) のくじ券 (抽選の案内) をお客様に見せたことを記録する.
// 当選口数は発行時に確定済みなのでこの mutation で結果は変わらない.
// 「くじ券を発行した人数」と「実際に見せられた人数」を分けて測るための記録.
const MarkLotteryTicketAsShown = graphql(/* GraphQL */ `
	mutation MarkLotteryTicketAsShown($input: MarkLotteryTicketAsShownInput!) {
		markLotteryTicketAsShown(input: $input) {
			__typename
		}
	}
`)

// NOTE: `treatableStaff` name fragment is also declared in other files
const treatableStaffFragment = graphql(/* GraphQL */ `
	fragment treatableStaff on TreatableStaff {
		scrapingTargetSiteStaffId
		name
	}
`)

const isSlotUnavailableToCreateReservationError = (
	error: ApolloError
): boolean => {
	const graphqlErrors = error.graphQLErrors
	return graphqlErrors.some((graphqlError) => {
		const code = getExtensionsCode(graphqlError)
		if (!code) {
			return false
		}
		return isSlotUnavailableToCreateReservationErrorCode(code)
	})
}

const PAGE_VERSION = 2

type CanceledReservationV2FragmentType = DocumentType<
	typeof CanceledReservationV2
>

const buildIncentiveProgress = (
	incentiveType: IncentiveSummary["type"],
	incentivePrice: number
): IncentiveProgressInfo => {
	if (incentiveType === "GIFT" || incentiveType === "GIFT_CANDIDATE") {
		return {
			leftLabel: `${toStringWithCommas(incentivePrice)}pt 還元枠`,
		}
	}
	if (incentiveType === "LOTTERY") {
		return {
			leftLabel: `${toStringWithCommas(incentivePrice)}円分ポイント 抽選枠`,
		}
	}
	return {
		leftLabel: `¥${toStringWithCommas(incentivePrice)} OFF 枠`,
	}
}

const RereservationPageV2 = () => {
	const cancelID = useParams().cancel_id
	const apolloClient = useApolloClient()
	const navigate = useNavigate()
	const event = useEvent()

	/* states */
	const [CanceledReservationData, setCanceledReservationData] =
		useState<CanceledReservationV2FragmentType | null>(null)

	const [isAbortModalOpen, setIsAbortModalOpen] = useState(false)
	const [isDifferentStaffModalOpen, setIsDifferentStaffModalOpen] =
		useState(false)
	const [isImageLoadError, setIsImageLoadError] = useState(false)
	// header 画像が 404 等で読み込めない時は banner 枠ごと消す。
	// 枠を残すと broken image アイコンと alt テキストが出て見た目が崩れるため。
	const [isHeaderImageLoadError, setIsHeaderImageLoadError] = useState(false)

	const {
		getChunksAndExtend,
		chunks,
		recommendedSlots,
		needAnyReservableEquipmentToCreateReservation,
		needSameEquipmentAsPreviousToCreateReservation,
		staggeredSchedulingEnabled,
		isLoading,
		sevenDaysFrom,
		setSevenDaysFrom,
		resetAndRefetch,
	} = useChunks(PAGE_VERSION, cancelID, event.fetchedReservableSlots)

	const canceledReservation = useMemo(() => {
		if (!CanceledReservationData) return null
		return CanceledReservationData.canceledReservationV2
	}, [CanceledReservationData])

	const originalStaff = useMemo(() => {
		if (!CanceledReservationData) return null
		return CanceledReservationData.canceledReservationV2.canceledReservation
			.staff
	}, [CanceledReservationData, canceledReservation])

	// 他スタッフ枠の表示条件: サロン設定が許可 && 顧客が新規 (visit_count < 1 = 来店0回)。
	// どちらか一方でも満たさなければ非表示。
	const allowsDifferentStaffReservation = useMemo(() => {
		if (!canceledReservation) return false
		const cr = canceledReservation.canceledReservation
		return (
			cr.enabledFeatures.allowsDifferentStaffReservation && cr.visitCount < 1
		)
	}, [canceledReservation])

	const enableAbortEncouragingReservationButton = useMemo(() => {
		if (!CanceledReservationData) return false
		return CanceledReservationData.canceledReservationV2.canceledReservation
			.enabledFeatures.enableAbortEncouragingReservationButton
	}, [canceledReservation])

	const enableSlotCaching = useMemo(() => {
		if (!CanceledReservationData) return false
		return CanceledReservationData.canceledReservationV2.canceledReservation
			.enabledFeatures.enableSlotCaching
	}, [canceledReservation])

	// 前回予約が指名なしで、店舗の feature flag (HIDE_STAFF_FOR_NOT_NOMINATED_RERESERVATION) が ON のとき true。
	// 上部カードと確認モーダルの「指名スタッフ」欄を出さない (BE が判定して返す)
	const hidesStaffDueToNotNominated = useMemo(() => {
		if (!CanceledReservationData) return false
		return CanceledReservationData.canceledReservationV2.canceledReservation
			.hidesStaffDueToNotNominated
	}, [CanceledReservationData])

	// const treatableStaffsName = useMemo(() => {
	// 	if (!CanceledReservationData) return []
	// 	return CanceledReservationData.canceledReservationV2.canceledReservation
	// 		.treatableStaffs.map((staff) => {
	// 			return getFragmentData(treatableStaffName, staff)
	// 		})
	// }, [canceledReservation])

	const selectedMenuDurationMinute = useMemo((): number => {
		return (
			CanceledReservationData?.canceledReservationV2.canceledReservation
				.durationMinute ?? DEFAULT_DURATION_MINUTE
		)
	}, [CanceledReservationData])

	const price = useMemo((): string => {
		return (
			"¥" +
			toStringWithCommas(
				CanceledReservationData?.canceledReservationV2.canceledReservation
					.price ?? 0
			)
		)
	}, [CanceledReservationData])

	// 前回予約で使用していた設備IDのリスト（needSameEquipmentAsPreviousToCreateReservation の場合に使用）
	const previousEquipmentIds = useMemo((): string[] => {
		return (
			CanceledReservationData?.canceledReservationV2.canceledReservation
				.equipmentIds ?? []
		)
	}, [CanceledReservationData])

	useEffect(() => {
		getChunksAndExtend(sevenDaysFrom, cancelID)
	}, [cancelID, getChunksAndExtend, sevenDaysFrom])

	useEffect(() => {
		if (!cancelID) {
			return
		}

		const context: ApolloContext = {
			fetchOptions: {
				skipLoading: true,
			},
		}

		apolloClient
			.query({
				query: CanceledReservationV2,
				variables: {
					param: {
						cancelId: cancelID,
					},
				},
				fetchPolicy: "no-cache",
				context,
			})
			.then((res) => {
				if (res.data.canceledReservationV2.isRereserved) {
					navigate(getCompletedPath(cancelID, PAGE_VERSION))
					return
				}

				setCanceledReservationData(res.data)
				event.fetchedCancelData(
					res.data.canceledReservationV2.canceledReservation.enabledFeatures
						.allowsDifferentStaffReservation,
					res.data.canceledReservationV2.canceledReservation.visitCount,
					res.data.canceledReservationV2.canceledReservation.enabledFeatures
						.enableAbortEncouragingReservationButton,
					res.data.canceledReservationV2.canceledReservation.enabledFeatures
						.enableSlotCaching,
					res.data.canceledReservationV2.canceledReservation.treatableStaffs.map(
						(staff: FragmentType<typeof treatableStaffFragment>) =>
							getFragmentData(treatableStaffFragment, staff)
								.scrapingTargetSiteStaffId
					)
				)
			})
	}, [cancelID, apolloClient, navigate])

	const salon = CanceledReservationData
		? CanceledReservationData.canceledReservationV2.salon
		: null

	// true の場合、請求転換 (キャンセル料を免除して再予約を案内した) の再予約なので、
	// hidesCancelPolicy に関わらず確認モーダルに特殊キャンセルポリシー (再キャンセルは免除なし) を表示する。
	// 判定は cancy 側 (message_kind=CLAIMING_CONVERSION かつ feature flag ON) で、完了画面・SMS・メールと同じ
	const useClaimingConversionRecancelPolicy =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.useClaimingConversionRecancelPolicy ?? false

	// 請求転換の再予約は、cancy が前回予約の金額 (0円かどうか) で片方だけ埋めて返すので、値が入っている方を使う。
	// それ以外は完了画面 (RereservationCompletePageV2) と同じ基準で、メニューありなら「メニューあり用」、
	// メニューなし (0円予約) なら「メニューなし用」のキャンセル料テキストを使う
	const hasMenuOrCoupon =
		(CanceledReservationData?.canceledReservationV2.canceledReservation
			.menuOrCouponNames.length ?? 0) > 0
	const cancelConditionsTexts = useMemo(() => {
		if (useClaimingConversionRecancelPolicy) {
			return pickClaimingConversionCancelConditionTexts(
				salon?.cancelConditionTextsForMenu,
				salon?.cancelConditionTextsForNoMenu
			)
		}
		if (hasMenuOrCoupon) {
			return salon?.cancelConditionTextsForMenu ?? []
		}
		return salon?.cancelConditionTextsForNoMenu ?? []
	}, [useClaimingConversionRecancelPolicy, hasMenuOrCoupon, salon])

	const menuOrCouponNames = CanceledReservationData?.canceledReservationV2
		.canceledReservation.menuOrCouponNames ?? ["メニューなし"]

	const menuTexts = getMenuNameTextsWithPriceAndMinutes(
		menuOrCouponNames,
		CanceledReservationData?.canceledReservationV2.canceledReservation.price ??
			0,
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.durationMinute ?? 0
	)

	const [createRereservationMutation, createRereservationMutationResult] =
		useMutation(CreateRereservationV2)

	const [
		createUenoRereservationRequestMutation,
		createUenoRereservationRequestMutationResult,
	] = useMutation(CreateUenoRereservationRequest)

	// 記録が落ちても当選口数は変わらず、お客様に見せる画面も変わらないので、
	// 失敗しても再予約の操作は止めない (onError で握って無視する).
	const [markLotteryTicketAsShownMutation] = useMutation(
		MarkLotteryTicketAsShown,
		{ onError: () => undefined }
	)

	// 上野クリニック(スプレッドシート)の店舗では、予約を確定せず希望枠の「受付」だけを行う
	const isUenoClinic = isUenoClinicDataSource(
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.dataSource
	)

	// true の場合、前回予約の詳細 (メニュー・スタッフ・予約番号) を表示せず、
	// お名前・前回来店予定日・電話番号のみ表示する (nabeko が data_source から導出)
	const hidesReservationDetails =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.enabledFeatures.hidesReservationDetails ?? false

	// true の場合、予約確定の確認モーダルに「※次回予約メニューの場合、通常価格でのご案内と
	// なることがございます」の注意書きを表示する (nabeko が feature flag から導出)
	const showsNextReservationMenuRegularPriceNote =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.enabledFeatures.showsNextReservationMenuRegularPriceNote ?? false

	// true の場合、予約確定の確認モーダルにキャンセルポリシー (キャンセル料率の一覧) を
	// 表示しない。現在 nabeko は全店舗で true (非表示) を返す
	// (ただし請求転換の再予約では useClaimingConversionRecancelPolicy が優先され、特殊キャンポリを表示する)
	const hidesCancelPolicy =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.enabledFeatures.hidesCancelPolicy ?? false

	// GIFT incentive (えらべるPay のポイント還元) 付きの予約なら還元ポイント数、
	// DISCOUNT や incentive なしなら null。確認モーダルの注意書きの出し分けに使う
	const giftIncentivePoint = useMemo(() => {
		const incentive =
			CanceledReservationData?.canceledReservationV2.canceledReservation
				.incentive ?? null
		if (incentive == null) return null
		const summary = unmaskIncentive(incentive)
		return summary.type === "GIFT" ? summary.price : null
	}, [CanceledReservationData])

	// GIFT_CANDIDATE (予算ありギフト) 付きの予約ならお渡しするポイント数、
	// それ以外は null。確認モーダルで「予算に達し次第終了」の注意書きを出す判定に使う
	const giftCandidatePoint = useMemo(() => {
		const incentive =
			CanceledReservationData?.canceledReservationV2.canceledReservation
				.incentive ?? null
		if (incentive == null) return null
		const summary = unmaskIncentive(incentive)
		return summary.type === "GIFT_CANDIDATE" ? summary.price : null
	}, [CanceledReservationData])

	// incentive の masked ref. バナー (rereservationIncentiveBanner) と
	// 確認モーダルの抽選注意書き (lotteryNoteSection) 両方の fragment ref を運ぶ.
	// 中身の解釈は各コンポーネントが自分の fragment を unmask して行う (fragment collocation)
	const incentiveFragment =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.incentive ?? null

	const [reservationConfirmDialogOpen, setReservationConfirmDialogOpen] =
		useState<boolean>(false)

	const [startTime, setStartTime] = useState<Date | null>(null)
	const [equipmentIds, setEquipmentIds] = useState<string[]>([])
	const [confirmationDetails, setConfirmationDetails] =
		useState<ReservationDetails | null>(null)

	// 上野クリニック: 顧客が選択中の希望日時 (最大2枠)
	const [uenoSelectedSlots, setUenoSelectedSlots] = useState<Date[]>([])

	const showReservationConfirm = useCallback(
		(
			startTime: Date,
			selectedStaffId: string,
			selectedStaffName: string,
			equipmentIds: string[]
		) => {
			if (isUenoClinic) {
				// 上野クリニックは予約を確定せず希望枠の「受付」を行うため、
				// 確定モーダルは開かず枠を選択/解除する (最大2枠)
				setUenoSelectedSlots((prev) => {
					const exists = prev.some((d) => d.getTime() === startTime.getTime())
					if (exists) {
						return prev.filter((d) => d.getTime() !== startTime.getTime())
					}
					if (prev.length >= 2) {
						window.alert("ご希望日時は最大2つまで選択できます")
						return prev
					}
					return [...prev, startTime].sort((a, b) => a.getTime() - b.getTime())
				})
				return
			}

			const datetime = `${formatJstDateUntilMinute(startTime)}`

			setEquipmentIds(equipmentIds)
			setConfirmationDetails({
				datetime,
				menuOrCouponNames,
				duration: `${selectedMenuDurationMinute}分`,
				originalStaffId: originalStaff?.scrapingTargetSiteId ?? "",
				hidesStaffDueToNotNominated,
				staff: {
					scrapingTargetSiteStaffId: selectedStaffId,
					name: selectedStaffName,
				},
				cancelConditionsTexts,
				price,
				showsNextReservationMenuRegularPriceNote,
				hidesCancelPolicy,
				useClaimingConversionRecancelPolicy,
				giftIncentivePoint,
				giftCandidatePoint,
				incentive: incentiveFragment,
			})
			setStartTime(startTime)
			event.openCreatingReservationPopup(startTime, selectedStaffId)
			setReservationConfirmDialogOpen(true)
		},
		[
			isUenoClinic,
			menuOrCouponNames,
			cancelConditionsTexts,
			selectedMenuDurationMinute,
			price,
			showsNextReservationMenuRegularPriceNote,
			hidesCancelPolicy,
			useClaimingConversionRecancelPolicy,
			giftIncentivePoint,
			giftCandidatePoint,
			incentiveFragment,
		]
	)

	// 上野クリニック: 選択した希望日時 (最大2枠) を「受付」として送信する
	const onSubmitUenoRereservationRequest = useCallback(() => {
		if (!cancelID || uenoSelectedSlots.length === 0) return

		const context: ApolloContext = {
			disableErrorDialog: true,
			fetchOptions: {
				skipLoading: true,
			},
		}

		createUenoRereservationRequestMutation({
			variables: {
				input: {
					cancelId: cancelID,
					firstStartTime: getJSTISOString(uenoSelectedSlots[0]),
					secondStartTime:
						uenoSelectedSlots.length >= 2
							? getJSTISOString(uenoSelectedSlots[1])
							: null,
				},
			},
			context,
		})
			.then(() => {
				navigate(getCompletedPath(cancelID, PAGE_VERSION))
			})
			.catch((error) => {
				window.alert(error.message)
			})
	}, [
		cancelID,
		uenoSelectedSlots,
		createUenoRereservationRequestMutation,
		navigate,
	])

	const isCreatingRereservation = useMemo(() => {
		return (
			createRereservationMutationResult.loading ||
			createUenoRereservationRequestMutationResult.loading
		)
	}, [
		createRereservationMutationResult.loading,
		createUenoRereservationRequestMutationResult.loading,
	])

	const sendRereservationGAEvents = useCallback(
		(
			startTime: Date,
			selectedStaffId: string,
			originalStaff: { scrapingTargetSiteId: string } | null,
			isAbortModalOpen: boolean,
			isDifferentStaffModalOpen: boolean
		) => {
			event.confirmCreatingRereservation(startTime, selectedStaffId)

			if (isAbortModalOpen) {
				event.confirmCreatingRereservationInAbortPopup(
					startTime,
					selectedStaffId
				)
			}

			if (isDifferentStaffModalOpen) {
				event.confirmCreatingRereservationInDifferentStaffs(
					originalStaff?.scrapingTargetSiteId ?? "",
					selectedStaffId
				)
			}
		},
		[]
	)

	const onConfirmReservation = useCallback(
		(selectedStaffId: string, staffName: string) => {
			if (!startTime || !confirmationDetails || !cancelID) return

			sendRereservationGAEvents(
				startTime,
				selectedStaffId,
				originalStaff,
				isAbortModalOpen,
				isDifferentStaffModalOpen
			)

			const context: ApolloContext = {
				disableErrorDialog: true,
				fetchOptions: {
					skipLoading: true,
				},
			}

			// NOTE: 上野クリニックは確定モーダルを開かず希望枠の選択のみ行うため、
			// ここには到達しない (受付の送信は onSubmitUenoRereservationRequest)
			createRereservationMutation({
				variables: {
					input: {
						cancelId: cancelID,
						scrapingTargetSiteStaffId: selectedStaffId,
						staffName: staffName,
						startTime: getJSTISOString(startTime),
						equipmentIds: equipmentIds,
					},
				},
				context,
			})
				.then(() => {
					setReservationConfirmDialogOpen(false)
					setIsAbortModalOpen(false)
					setIsDifferentStaffModalOpen(false)
					navigate(getCompletedPath(cancelID, PAGE_VERSION))
				})
				.catch((error) => {
					if (
						isSlotUnavailableToCreateReservationError(error) &&
						enableSlotCaching
					) {
						setReservationConfirmDialogOpen(false)
						setIsAbortModalOpen(false)
						setIsDifferentStaffModalOpen(false)
						event.openAlreadyReservedPopup(startTime)
						window.alert(error.message)
						event.closeAlreadyReservedPopup()
						resetAndRefetch(cancelID)
					} else {
						window.alert(error.message)
					}
				})
		},
		[
			cancelID,
			createRereservationMutation,
			startTime,
			confirmationDetails,
			equipmentIds,
			navigate,
			resetAndRefetch,
			isAbortModalOpen,
		]
	)

	const handleAbortConfirm = useCallback(async () => {
		if (!window.confirm("配信を停止しますが、よろしいですか？")) {
			return
		}

		event.clickAbortButton()

		await apiService
			.post(
				`/public/abort_encouraging_rereservation_message/cancel/${cancelID}`,
				undefined
			)
			.then(() => {
				alert("配信を停止しました。")
				setIsAbortModalOpen(false)
			})
			.catch(() => {
				alert("エラーが発生しました。時間が経ってから再度お試しください。")
			})
	}, [])

	const salon_name = salon?.name ?? ""
	const salon_icon_url = salon?.iconUrl ?? ""
	const salon_header_image_url = salon?.headerImageUrl ?? ""

	// hidesReservationDetails=true (上野等) は前回予約の詳細 (originPrice 含む) を
	// 表示しない契約のため、originPrice を描画する incentive バナーも表示しない
	const showsIncentiveBanner =
		incentiveFragment != null && !hidesReservationDetails

	const customerName =
		CanceledReservationData?.canceledReservationV2.customer.name ?? ""
	const customerPhoneNumber =
		CanceledReservationData?.canceledReservationV2.customer.phoneNumber ?? null

	const reserveId =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.reserveId ?? null
	const visitDate =
		CanceledReservationData?.canceledReservationV2.canceledReservation
			.visitDate ?? null
	const visitDateText = visitDate
		? formatGraphQLDayToJapaneseMonthDay(visitDate)
		: null

	// design 参照: frontend/design/public/rereservation.pen Frame rPg4N "Week Selector" 内 "progressBox" (id: uxSrX)
	// 右ラベルとゲージ幅は ReservableSlotsV2 側で chunks から計算する.
	// 上野クリニックは希望枠「受付」フローで incentive (OFF/還元) を持たないため、残り枠ゲージは表示しない.
	let incentiveProgress: IncentiveProgressInfo | null = null
	if (incentiveFragment != null && !isUenoClinic) {
		const summary = unmaskIncentive(incentiveFragment)
		incentiveProgress = buildIncentiveProgress(summary.type, summary.price)
	}

	// 抽選キャンペーン (LOTTERY) の規約は、抽選バナーを出している画面でだけ一番下に出す.
	// バナーを出さない (= 抽選を訴求していない) 画面に規約だけが出ることは無いようにする.
	let showsLotteryTerms = false
	if (incentiveFragment != null && showsIncentiveBanner) {
		showsLotteryTerms = unmaskIncentive(incentiveFragment).type === "LOTTERY"
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<img src={dateImage} alt="カレンダー" className={styles.headerIcon} />
				<div className={styles.headerText}>ワンクリック再予約フォーム</div>
			</div>
			{/* 大きい header banner: salon.headerImageUrl があり、かつ読み込めた時のみ表示.
			    salon icon + name は header の有無に関わらず常に下に表示する. */}
			{salon_header_image_url && !isHeaderImageLoadError && (
				<div className={styles.headerBanner}>
					<img
						src={salon_header_image_url}
						alt="salon_header"
						className={styles.headerBannerImage}
						onError={() => setIsHeaderImageLoadError(true)}
					/>
				</div>
			)}
			<div className={styles.mainSection}>
				<div
					className={`${styles.salonHeader} ${isImageLoadError ? styles.salonHeaderNoImage : ""}`}
				>
					{salon_icon_url && !isImageLoadError && (
						<img
							src={salon_icon_url}
							alt="salon_icon"
							className={styles.salonIcon}
							onError={() => setIsImageLoadError(true)}
						/>
					)}
					<div
						className={`${styles.salonTitle} ${!salon_icon_url || isImageLoadError ? styles.salonTitleEmphasis : ""}`}
					>
						{salon_name ?? "ワンクリック再予約フォーム"}
					</div>
				</div>
				{incentiveFragment != null && showsIncentiveBanner && (
					<RereservationIncentiveBanner
						incentive={incentiveFragment}
						cancelId={
							CanceledReservationData!.canceledReservationV2.canceledReservation
								.cancelId
						}
						customerName={customerName}
						originPrice={
							CanceledReservationData!.canceledReservationV2.canceledReservation
								.price
						}
						onLotteryShown={() => {
							void markLotteryTicketAsShownMutation({
								variables: {
									input: {
										cancelId:
											CanceledReservationData!.canceledReservationV2
												.canceledReservation.cancelId,
									},
								},
							})
						}}
					/>
				)}
				<div className={styles.infoCard}>
					{/* 顧客名ヘッダーは incentive バナーを表示する時のみ非表示にする (現行 UI を維持) */}
					{!showsIncentiveBanner && (
						<>
							<div className={styles.cardHeader}>{customerName}様</div>
							<div className={styles.divider}></div>
						</>
					)}
					{/* hidesReservationDetails=true (上野等) はメニュー・スタッフ・予約番号のデータを持たないため表示しない */}
					{!hidesReservationDetails && (
						<div className={styles.cardContent}>
							<div className={styles.pinkLabel}>ご予約メニュー</div>
							<div className={styles.multilineText}>{menuTexts.join("\n")}</div>
						</div>
					)}

					{/* 指名なし (is_nominated=false) で feature flag HIDE_STAFF_FOR_NOT_NOMINATED_RERESERVATION が ON の店舗は
					    「指名していないのに【前回指名】」と読ませないためスタッフ欄ごと出さない (BE が判定して返す) */}
					{!hidesReservationDetails && !hidesStaffDueToNotNominated && (
						<div className={styles.cardContent}>
							<div className={styles.pinkLabel}>指名スタッフ</div>
							<div className={styles.contentText}>
								{originalStaffLabel(
									originalStaff?.scrapingTargetSiteId,
									originalStaff?.name
								)}
							</div>
						</div>
					)}

					{visitDateText && (
						<div className={styles.cardContent}>
							<div className={styles.pinkLabel}>前回来店予定日</div>
							<div className={styles.contentText}>{visitDateText}</div>
						</div>
					)}

					{!hidesReservationDetails && reserveId && (
						<div className={styles.cardContent}>
							<div className={styles.pinkLabel}>前回ご予約番号</div>
							<div className={styles.contentText}>{reserveId}</div>
						</div>
					)}

					{customerPhoneNumber && (
						<div className={styles.cardContent}>
							<div className={styles.pinkLabel}>電話番号</div>
							<div className={styles.contentText}>{customerPhoneNumber}</div>
						</div>
					)}
				</div>
				<div className={styles.reservableSlotsText}>
					ご希望の再予約日時を選択してください
				</div>
			</div>

			<div className={styles.slots_wrapper}>
				<ReservableSlotsV2
					maskedChunks={chunks}
					showReservationConfirm={showReservationConfirm}
					durationMinute={selectedMenuDurationMinute}
					needAnyReservableEquipmentToCreateReservation={
						needAnyReservableEquipmentToCreateReservation
					}
					needSameEquipmentAsPreviousToCreateReservation={
						needSameEquipmentAsPreviousToCreateReservation
					}
					previousEquipmentIds={previousEquipmentIds}
					staggeredSchedulingEnabled={staggeredSchedulingEnabled}
					loading={isLoading}
					sevenDaysFrom={sevenDaysFrom}
					setSevenDaysFrom={setSevenDaysFrom}
					loadChunksFrom={getChunksAndExtend}
					originalStaff={originalStaff}
					allowsDifferentStaffReservation={allowsDifferentStaffReservation}
					treatableStaffs={
						CanceledReservationData?.canceledReservationV2.canceledReservation
							.treatableStaffs ?? []
					}
					isDifferentStaffModalOpen={isDifferentStaffModalOpen}
					setIsDifferentStaffModalOpen={setIsDifferentStaffModalOpen}
					incentiveProgress={incentiveProgress}
					selectedStartTimes={isUenoClinic ? uenoSelectedSlots : undefined}
				/>
			</div>

			{/* 上野クリニック: 選択中の希望日時 (最大2枠) と受付ボタン */}
			{isUenoClinic && (
				<div className={styles.uenoRequestPanel}>
					<div className={styles.uenoRequestTitle}>
						選択中のご希望日時（最大2つ）
					</div>
					{uenoSelectedSlots.length === 0 ? (
						<div className={styles.uenoRequestEmpty}>
							カレンダーからご希望の日時を選択してください
						</div>
					) : (
						uenoSelectedSlots.map((slot, i) => (
							<button
								type="button"
								key={slot.getTime()}
								className={styles.uenoSelectedSlot}
								onClick={() =>
									setUenoSelectedSlots((prev) =>
										prev.filter((d) => d.getTime() !== slot.getTime())
									)
								}
							>
								ご希望日時{i + 1}: {formatJstDateUntilMinute(slot)}
								（タップで解除）
							</button>
						))
					)}
					<button
						type="button"
						className={styles.uenoSubmitButton}
						disabled={uenoSelectedSlots.length === 0 || isCreatingRereservation}
						onClick={onSubmitUenoRereservationRequest}
					>
						{isCreatingRereservation
							? "送信中..."
							: "この内容で予約を受け付ける"}
					</button>
					<div className={styles.uenoRequestNote}>
						※ この時点ではご予約は確定しません。確定は追ってご連絡いたします。
					</div>
				</div>
			)}

			<ModalDialog
				isOpen={reservationConfirmDialogOpen}
				onOpenChange={(open) => {
					if (isCreatingRereservation) {
						// 再予約作成 mutation 実行中は、モーダル背景を押してもモーダルが閉じないようにする
						// TODO: 本当は z-index によってモーダル背景を押せないようにするのが正しいかも (なお、作成中 modal の ❌ ボタンは押せないようになっているので、そっちは大丈夫な模様)
						return
					}
					setReservationConfirmDialogOpen(open)
					if (!open) {
						event.closeCreatingReservationPopup()
					}
				}}
			>
				{confirmationDetails && (
					<ReservationConfirmModalContent
						reservationDetails={confirmationDetails}
						onConfirm={onConfirmReservation}
						onCancel={() => {
							setReservationConfirmDialogOpen(false)
							event.closeCreatingReservationPopup()
						}}
						isCreatingRereservation={isCreatingRereservation}
					/>
				)}
			</ModalDialog>

			{enableAbortEncouragingReservationButton && (
				<div className={styles.abort_section}>
					<div className={styles.abort_text}>
						予約案内が不要な場合は
						<button
							className={styles.abort_button}
							onClick={() => setIsAbortModalOpen(true)}
						>
							こちら
						</button>
					</div>
				</div>
			)}
			<ModalDialog
				isOpen={isAbortModalOpen}
				onOpenChange={(open) => {
					setIsAbortModalOpen(open)
					if (!open) {
						event.closeAbortPopup()
					}
				}}
				contentClassName={styles.ModalContent}
			>
				<AbortModalContent
					recommendedSlots={recommendedSlots}
					onAbortConfirm={handleAbortConfirm}
					onCancel={() => setIsAbortModalOpen(false)}
					onRecommendedSlotReservation={showReservationConfirm}
					durationMinute={selectedMenuDurationMinute}
				/>
			</ModalDialog>

			{showsLotteryTerms && <LotteryTermsSection />}
		</div>
	)
}

export default RereservationPageV2
